import React, { useState } from 'react';
import apiService from '../../services/apiService';
import Modal from '../ui/Modal';
import { useI18n } from '../../utils/i18n';
import { useToast } from '../ToastProvider';
import { useConfirm } from '../ui/ConfirmDialog';

// #530 (Scheibe 3 aus #310): Kommentare anlegen, bearbeiten und loeschen
// (der selbst getippte Kommentar darf in die Offline-Warteschlange, #322),
// die KI-Zusammenfassung des Tickets und die KI-Kurzfassung der
// Beschreibung (#208). Code unveraendert aus TicketDetail.js uebernommen,
// Muster aus #312/#471/#528.
//
// Den Hook ruft TicketDetail auf, die Dialoge rendern nur. Abbrechen leert
// den Kommentar-Entwurf nicht; das Testnetz haelt es fest.
export function useKommentare({ ticket, currentTicket, setCurrentTicket, onTicketUpdate, setError, setHasChanges, loadTicketHistory }) {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const confirm = useConfirm();
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [showAddCommentModal, setShowAddCommentModal] = useState(false);
  const [showEditCommentModal, setShowEditCommentModal] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [editingCommentTicketId, setEditingCommentTicketId] = useState(null);
  const [updatingComment, setUpdatingComment] = useState(false);
  const [aiSummarizing, setAiSummarizing] = useState(false);
  // #208: Beschreibung per LLM zusammenfassen (Vorschlag → Bestätigen)
  const [descSummarizeOpen, setDescSummarizeOpen] = useState(false);
  const [descSummarizing, setDescSummarizing] = useState(false);
  const [descSuggestion, setDescSuggestion] = useState('');
  const [descApplying, setDescApplying] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiError, setAiError] = useState(null);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setAddingComment(true);
      // Always target the latest resolved ticket context to avoid stale prop id
      const targetId = (currentTicket?.id || ticket?.id);
      // #322: Nur dieser Weg -- der Kommentar, den jemand selbst tippt und
      // abschickt -- darf in die Offline-Warteschlange. Kommentare, die als
      // Nebenwirkung eines anderen Vorgangs entstehen, nicht.
      const ergebnis = await apiService.addCommentToTicket(targetId, newComment.trim(), false, { offlineFaehig: true });
      setNewComment('');
      setShowAddCommentModal(false); // Close modal
      if (ergebnis?.eingereiht) {
        // Kein Netz: Der Text liegt gesichert im Browser und geht raus,
        // sobald wieder Verbindung besteht. Nichts neu zu laden.
        pushToast({
          message: t('ticketDetail.commentQueued', 'Kein Netz — der Kommentar ist gespeichert und wird gesendet, sobald die Verbindung zurück ist.'),
          type: 'info',
          timeoutMs: 0
        });
        return;
      }
      setHasChanges(true); // Mark as changed
      await loadTicketHistory(); // Reload history
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Fehler beim Hinzufügen des Kommentars');
    } finally {
      setAddingComment(false);
    }
  };

  const handleOpenEditCommentModal = (item) => {
    try {
      const match = (item?.id || '').toString().match(/^comment-(\d+)/);
      const commentId = match ? parseInt(match[1], 10) : null;
      if (!commentId) return;
      setEditingCommentId(commentId);
      setEditingCommentContent(item.content || '');
      // Store the originating ticket for cross-ticket updates in aggregated view
      setEditingCommentTicketId(item.ticketId || currentTicket.id || ticket.id);
      setShowEditCommentModal(true);
    } catch (_) {}
  };

  const handleUpdateComment = async (e) => {
    e.preventDefault();
    if (!editingCommentId || !editingCommentContent.trim()) return;
    try {
      setUpdatingComment(true);
      const targetTicketId = editingCommentTicketId || currentTicket?.id || ticket.id;
      await apiService.updateTicketComment(targetTicketId, editingCommentId, editingCommentContent.trim());
      setShowEditCommentModal(false);
      setEditingCommentId(null);
      setEditingCommentContent('');
      setEditingCommentTicketId(null);
      setHasChanges(true);
      await loadTicketHistory();
    } catch (error) {
      console.error('Error updating comment:', error);
      setError(t('ticketDetail.updateError', 'Fehler beim Aktualisieren des Kommentars'));
    } finally {
      setUpdatingComment(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!editingCommentId) return;
    const confirmed = await confirm({ message: t('ticketDetail.confirmDeleteComment', 'Diesen Kommentar wirklich löschen?'), danger: true });
    if (!confirmed) return;
    try {
      setUpdatingComment(true);
      const targetTicketId = editingCommentTicketId || ticket.id;
      await apiService.deleteTicketComment(targetTicketId, editingCommentId);
      setShowEditCommentModal(false);
      setEditingCommentId(null);
      setEditingCommentContent('');
      setEditingCommentTicketId(null);
      setHasChanges(true);
      await loadTicketHistory();
    } catch (error) {
      console.error('Error deleting comment:', error);
      setError(t('ticketDetail.deleteError', 'Fehler beim Löschen des Kommentars'));
    } finally {
      setUpdatingComment(false);
    }
  };

  const handleGenerateAiSummary = async () => {
    try {
      if (aiSummarizing) return;
      setAiError(null);
      setAiSummary('');
      setAiSummarizing(true);
      const res = await apiService.generateTicketSummary(ticket.id);
      setAiSummary(res?.answer || '');
    } catch (e) {
      console.error('Error generating AI summary:', e);
      setAiError(t('ticketDetail.aiSummaryError', 'Fehler beim Erstellen der KI-Zusammenfassung'));
    } finally {
      setAiSummarizing(false);
    }
  };

  const handleClearAiSummary = () => {
    setAiError(null);
    setAiSummary('');
  };

  // #208: Vorschlag holen (speichert nichts) — der Dialog zeigt ihn editierbar
  const handleSummarizeDescription = async () => {
    if (descSummarizing) return;
    setDescSuggestion('');
    setDescSummarizeOpen(true);
    setDescSummarizing(true);
    try {
      const res = await apiService.summarizeTicketDescription(ticket.id);
      setDescSuggestion(res?.suggestion || '');
    } catch (e) {
      console.error('Error summarizing description:', e);
      setDescSummarizeOpen(false);
      pushToast({
        message: e.response?.data?.aiDisabled
          ? t('ticketDetail.aiDisabled', 'KI-Funktionen sind deaktiviert.')
          : (e.response?.data?.error || t('ticketDetail.descSummarizeError', 'Fehler beim Zusammenfassen der Beschreibung.')),
        type: 'error'
      });
    } finally {
      setDescSummarizing(false);
    }
  };

  // #208: bestätigten (ggf. angepassten) Vorschlag übernehmen — die alte
  // Beschreibung sichert der Server als internen Kommentar
  const handleApplySummarizedDescription = async () => {
    const description = descSuggestion.trim();
    if (!description || descApplying) return;
    setDescApplying(true);
    try {
      const res = await apiService.applySummarizedDescription(ticket.id, description);
      setDescSummarizeOpen(false);
      const updated = res?.ticket || { ...currentTicket, description };
      setCurrentTicket(prev => ({ ...prev, ...updated }));
      if (onTicketUpdate) {
        try { onTicketUpdate(updated); } catch (_) {}
      }
      setHasChanges(true);
      pushToast({ message: t('ticketDetail.descSummarizeApplied', 'Beschreibung übernommen — das Original steht als interner Kommentar in der Historie.'), type: 'success' });
      // Historie neu laden, damit der Sicherungs-Kommentar sofort sichtbar ist
      try { await loadTicketHistory(); } catch (_) {}
    } catch (e) {
      console.error('Error applying summarized description:', e);
      pushToast({ message: e.response?.data?.error || t('ticketDetail.descSummarizeApplyError', 'Fehler beim Übernehmen der Beschreibung.'), type: 'error' });
    } finally {
      setDescApplying(false);
    }
  };

  return {
    newComment, setNewComment, addingComment, showAddCommentModal, setShowAddCommentModal, showEditCommentModal,
    setShowEditCommentModal, editingCommentContent, setEditingCommentContent, updatingComment, aiSummarizing, aiSummary,
    aiError, descSummarizeOpen, setDescSummarizeOpen, descSummarizing, descSuggestion, setDescSuggestion,
    descApplying, handleAddComment, handleOpenEditCommentModal, handleUpdateComment, handleDeleteComment, handleGenerateAiSummary,
    handleClearAiSummary, handleSummarizeDescription, handleApplySummarizedDescription
  };
}

export default function KommentarDialoge({ kommentare }) {
  const { t } = useI18n();
  const {
    newComment, setNewComment, addingComment, showAddCommentModal, setShowAddCommentModal, showEditCommentModal,
    setShowEditCommentModal, editingCommentContent, setEditingCommentContent, updatingComment, descSummarizeOpen, setDescSummarizeOpen,
    descSummarizing, descSuggestion, setDescSuggestion, descApplying, handleAddComment, handleUpdateComment,
    handleDeleteComment, handleApplySummarizedDescription
  } = kommentare;
  return (
    <>
        {/* #208: Beschreibung zusammenfassen — Vorschlag prüfen/anpassen, dann übernehmen */}
        <Modal
          open={descSummarizeOpen}
          onClose={() => { if (!descApplying) setDescSummarizeOpen(false); }}
          title={t('ticketDetail.descSummarizeTitle', 'Beschreibung zusammenfassen')}
          maxWidth="560px"
          closeLabel={t('common.close', 'Schließen')}
          footer={(
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setDescSummarizeOpen(false)} disabled={descApplying}>
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleApplySummarizedDescription}
                disabled={descSummarizing || descApplying || !descSuggestion.trim()}
              >
                {descApplying ? t('ticketDetail.descSummarizeApplying', 'Übernehme…') : t('ticketDetail.descSummarizeApply', 'Übernehmen')}
              </button>
            </>
          )}
        >
          {descSummarizing ? (
            <p className="section-description">{t('ticketDetail.descSummarizeLoading', 'Die KI fasst die Beschreibung zusammen…')}</p>
          ) : (
            <>
              <p className="section-description">
                {t('ticketDetail.descSummarizeHint', 'Vorschlag prüfen und bei Bedarf anpassen. Beim Übernehmen wird die bisherige Beschreibung automatisch als interner Kommentar gesichert.')}
              </p>
              <textarea
                className="form-textarea"
                rows={10}
                value={descSuggestion}
                onChange={(e) => setDescSuggestion(e.target.value)}
                disabled={descApplying}
                style={{ width: '100%', resize: 'vertical', minHeight: 160 }}
              />
            </>
          )}
        </Modal>

        {/* ADD COMMENT MODAL */}
        {showAddCommentModal && (
        <Modal
          open
          onClose={() => setShowAddCommentModal(false)}
          title={t('ticketDetail.addComment', 'Kommentar hinzufügen')}
          className="add-comment-modal"
          maxWidth="600px"
        >
              
              <form onSubmit={handleAddComment} className="comment-form">
                <div className="form-body">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={t('ticketDetail.enterComment', 'Neuen Kommentar eingeben...')}
                    rows="6"
                    disabled={addingComment}
                    className="form-textarea"
                  />
                </div>
                
                <div className="modal-actions">
                  <button 
                    type="button" 
                    onClick={() => setShowAddCommentModal(false)} 
                    className="btn btn-secondary"
                  >
                    {t('ticketDetail.cancel', 'Abbrechen')}
                  </button>
                  <button 
                    type="submit" 
                    disabled={addingComment || !newComment.trim()}
                    className="btn btn-primary"
                  >
                    {addingComment ? t('ticketDetail.adding', 'Hinzufügen...') : t('ticketDetail.addComment', 'Kommentar hinzufügen')}
                  </button>
                </div>
              </form>
        </Modal>
        )}

        {/* EDIT COMMENT MODAL */}
        {showEditCommentModal && (
        <Modal
          open
          onClose={() => setShowEditCommentModal(false)}
          title={t('ticketDetail.editCommentTitle', 'Kommentar bearbeiten')}
          className="add-comment-modal"
          maxWidth="600px"
        >
              
              <form onSubmit={handleUpdateComment} className="comment-form">
                <div className="form-body">
                  <textarea
                    value={editingCommentContent}
                    onChange={(e) => setEditingCommentContent(e.target.value)}
                    rows="6"
                    disabled={updatingComment}
                    className="form-textarea"
                    autoFocus
                  />
                </div>
                
                <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
                  <button 
                    type="button"
                    onClick={handleDeleteComment}
                    className="btn btn-danger"
                    disabled={updatingComment}
                  >
                    {updatingComment ? t('ticketDetail.deleting', 'Lösche...') : t('common.delete', 'Löschen')}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowEditCommentModal(false)} 
                    className="btn btn-secondary"
                    disabled={updatingComment}
                  >
                    {t('ticketDetail.cancel', 'Abbrechen')}
                  </button>
                  <button 
                    type="submit" 
                    disabled={updatingComment || !editingCommentContent.trim()}
                    className="btn btn-primary"
                  >
                    {updatingComment ? t('ticketDetail.adding', 'Hinzufügen...') : t('common.save', 'Speichern')}
                  </button>
                </div>
              </form>
        </Modal>
        )}
    </>
  );
}
